import React, { useState } from 'react';
import AttachmentUpload from './AttachmentUpload';
import FlightSearchModal from './FlightSearchModal';
import CommonOptionFields from './CommonOptionFields';

// Re-enabled 2026-07-22: live Duffel API key is now in place (matching
// DUFFEL_FLIGHT_SEARCH_ENABLED=true in .env) and verified against real
// airline pricing/availability - see the 2026-07-21 commit for the
// disable, and this one for the re-enable.
const FLIGHT_SEARCH_ENABLED = true;

// Shared by the Stops/Return Stops selects and by handleImportFlight -
// "3" doubles as "3+" since Duffel search results and manual entry alike
// are extremely unlikely to have more than that on a single leg.
const STOPS_OPTIONS = [
  { value: '', label: 'Not specified' },
  { value: '0', label: 'Nonstop' },
  { value: '1', label: '1 Stop' },
  { value: '2', label: '2 Stops' },
  { value: '3', label: '3+ Stops' }
];

const formatDurationMinutes = (minutes) => {
  if (minutes === null || minutes === undefined || isNaN(minutes)) return '';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours > 0 ? `${hours}h ` : ''}${mins}m`.trim();
};

// Keeps the connections array (layover airport + duration, one per stop)
// in sync with however many stops are selected - preserves what's already
// been entered/imported for the rows that still exist, adds blank rows if
// the agent increases the stop count, drops extra rows if they decrease it.
const resizeConnections = (connections, stopsValue) => {
  const targetLength = stopsValue === '' ? 0 : parseInt(stopsValue, 10);
  const current = connections || [];
  if (current.length === targetLength) return current;
  if (current.length > targetLength) return current.slice(0, targetLength);
  return [
    ...current,
    ...Array.from({ length: targetLength - current.length }, () => ({ airport: '', duration: '' }))
  ];
};

const FlightForm = ({ onSave, onCancel, initialData }) => {
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    // Existing saved components predate this field, so no value means the
    // single leg they already have is the one-way/outbound leg.
    tripType: initialData?.tripType || 'one_way',
    airline: initialData?.airline || '',
    flightNumber: initialData?.flightNumber || '',
    departureAirport: initialData?.departureAirport || '',
    departureDate: initialData?.departureDate || '',
    departureTime: initialData?.departureTime || '',
    arrivalAirport: initialData?.arrivalAirport || '',
    arrivalDate: initialData?.arrivalDate || '',
    arrivalTime: initialData?.arrivalTime || '',
    seats: initialData?.seats || '',
    confirmationNumber: initialData?.confirmationNumber || '',
    // Number of layovers on the outbound leg - '' means "not specified"
    // (older saved flights predate this field). See STOPS_OPTIONS below.
    stops: initialData?.stops !== undefined && initialData?.stops !== null ? String(initialData.stops) : '',
    // One entry per stop: { airport: 'DEN', duration: '2h 26m' }.
    connections: initialData?.connections || [],
    // Return leg - only used/shown when tripType is 'round_trip'.
    returnAirline: initialData?.returnAirline || '',
    returnFlightNumber: initialData?.returnFlightNumber || '',
    returnDepartureAirport: initialData?.returnDepartureAirport || '',
    returnDepartureDate: initialData?.returnDepartureDate || '',
    returnDepartureTime: initialData?.returnDepartureTime || '',
    returnArrivalAirport: initialData?.returnArrivalAirport || '',
    returnArrivalDate: initialData?.returnArrivalDate || '',
    returnArrivalTime: initialData?.returnArrivalTime || '',
    returnSeats: initialData?.returnSeats || '',
    returnConfirmationNumber: initialData?.returnConfirmationNumber || '',
    returnStops: initialData?.returnStops !== undefined && initialData?.returnStops !== null ? String(initialData.returnStops) : '',
    returnConnections: initialData?.returnConnections || [],
    liveTracking: initialData?.liveTracking !== false,
    description: initialData?.description || '',
    price: initialData?.price || '',
    isOption: initialData?.isOption || false,
    optionDescription: initialData?.optionDescription || '',
    paymentTiming: initialData?.paymentTiming || 'with_deposit',
    optionalDepositAmount: initialData?.optionalDepositAmount || '',
    depositRequired: initialData?.depositRequired || false,
    depositAmount: initialData?.depositAmount || '',
    depositDueDate: initialData?.depositDueDate || '',
    agentNotes: initialData?.agentNotes || '',
    agentCommission: initialData?.agentCommission ?? null,
    photos: initialData?.photos || [],
    documents: initialData?.documents || []
  });

  // Switching to Round-Trip pre-fills the return leg's airports as the
  // reverse of the outbound leg (the common case) - still fully editable.
  const handleTripTypeChange = (tripType) => {
    setFormData((prev) => ({
      ...prev,
      tripType,
      returnDepartureAirport: prev.returnDepartureAirport || prev.arrivalAirport,
      returnArrivalAirport: prev.returnArrivalAirport || prev.departureAirport
    }));
  };

  // Prefill the form from a selected Amadeus flight-search result (see
  // FlightSearchModal.js / backend's POST /api/ai-tools/flight-search).
  // Agent can still edit every field afterward before saving.
  const handleImportFlight = (flight) => {
    setFormData((prev) => ({
      ...prev,
      title: prev.title || `Flight to ${flight.outbound.arrivalAirport}`,
      tripType: flight.inbound ? 'round_trip' : 'one_way',
      airline: flight.outbound.airline,
      flightNumber: flight.outbound.flightNumber,
      departureAirport: flight.outbound.departureAirport,
      departureDate: flight.outbound.departureDate,
      departureTime: flight.outbound.departureTime,
      arrivalAirport: flight.outbound.arrivalAirport,
      arrivalDate: flight.outbound.arrivalDate,
      arrivalTime: flight.outbound.arrivalTime,
      stops: String(Math.min(flight.outbound.stops ?? 0, 3)),
      connections: (flight.outbound.connections || []).map((c) => ({
        airport: c.airport,
        duration: formatDurationMinutes(c.durationMinutes)
      })),
      price: flight.price.total,
      ...(flight.inbound ? {
        returnAirline: flight.inbound.airline,
        returnFlightNumber: flight.inbound.flightNumber,
        returnDepartureAirport: flight.inbound.departureAirport,
        returnDepartureDate: flight.inbound.departureDate,
        returnDepartureTime: flight.inbound.departureTime,
        returnArrivalAirport: flight.inbound.arrivalAirport,
        returnArrivalDate: flight.inbound.arrivalDate,
        returnArrivalTime: flight.inbound.arrivalTime,
        returnStops: String(Math.min(flight.inbound.stops ?? 0, 3)),
        returnConnections: (flight.inbound.connections || []).map((c) => ({
          airport: c.airport,
          duration: formatDurationMinutes(c.durationMinutes)
        }))
      } : {})
    }));
    setShowSearchModal(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const isRoundTrip = formData.tripType === 'round_trip';
    onSave({
      ...formData,
      price: parseFloat(formData.price) || 0,
      paymentTiming: formData.paymentTiming,
      optionalDepositAmount: formData.optionalDepositAmount ? parseFloat(formData.optionalDepositAmount) : null,
      stops: formData.stops !== '' ? parseInt(formData.stops, 10) : null,
      // Don't carry stale return-leg data if the agent switched back to
      // One-Way after having entered return details.
      returnAirline: isRoundTrip ? formData.returnAirline : '',
      returnFlightNumber: isRoundTrip ? formData.returnFlightNumber : '',
      returnDepartureAirport: isRoundTrip ? formData.returnDepartureAirport : '',
      returnDepartureDate: isRoundTrip ? formData.returnDepartureDate : '',
      returnDepartureTime: isRoundTrip ? formData.returnDepartureTime : '',
      returnArrivalAirport: isRoundTrip ? formData.returnArrivalAirport : '',
      returnArrivalDate: isRoundTrip ? formData.returnArrivalDate : '',
      returnArrivalTime: isRoundTrip ? formData.returnArrivalTime : '',
      returnSeats: isRoundTrip ? formData.returnSeats : '',
      returnConfirmationNumber: isRoundTrip ? formData.returnConfirmationNumber : '',
      returnStops: isRoundTrip && formData.returnStops !== '' ? parseInt(formData.returnStops, 10) : null,
      returnConnections: isRoundTrip ? formData.returnConnections : []
    });
  };

  // Changing the Stops/Return Stops select resizes the matching
  // connections array (see resizeConnections above) so there's always
  // exactly one editable layover row per stop.
  const handleStopsChange = (stopsField, connectionsField, value) => {
    setFormData((prev) => ({
      ...prev,
      [stopsField]: value,
      [connectionsField]: resizeConnections(prev[connectionsField], value)
    }));
  };

  const handleConnectionChange = (connectionsField, index, key, value) => {
    setFormData((prev) => {
      const updated = [...prev[connectionsField]];
      updated[index] = { ...updated[index], [key]: value };
      return { ...prev, [connectionsField]: updated };
    });
  };

  // One row per stop - shown right under the Stops/Return Stops select
  // whenever at least one stop is selected. See FlightSearchModal.js for
  // where the imported values (airport + duration) originally come from.
  const renderConnectionsEditor = (connections, connectionsField) => (
    <div className="flight-connections">
      {connections.map((connection, index) => (
        <div className="form-row flight-connection-row" key={index}>
          <div className="form-group">
            <label>Layover {connections.length > 1 ? index + 1 : ''} Airport</label>
            <input
              type="text"
              value={connection.airport}
              onChange={(e) => handleConnectionChange(connectionsField, index, 'airport', e.target.value)}
              placeholder="e.g., DEN"
            />
          </div>
          <div className="form-group">
            <label>Layover {connections.length > 1 ? index + 1 : ''} Duration</label>
            <input
              type="text"
              value={connection.duration}
              onChange={(e) => handleConnectionChange(connectionsField, index, 'duration', e.target.value)}
              placeholder="e.g., 2h 15m"
            />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <>
    <form onSubmit={handleSubmit} className="component-form flight-form">
      <div className="form-section">
        <div className="flight-form-section-header">
          <h4>Flight Information</h4>
          <button
            type="button"
            className="btn btn-secondary btn-search-flights"
            onClick={() => setShowSearchModal(true)}
            disabled={!FLIGHT_SEARCH_ENABLED}
            title={FLIGHT_SEARCH_ENABLED ? undefined : 'Flight search is temporarily disabled - please enter flight details manually for now'}
          >
            🔍 Search Flights {!FLIGHT_SEARCH_ENABLED && '(Coming Soon)'}
          </button>
        </div>

        <div className="form-group">
          <label>Title/Description*</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({...formData, title: e.target.value})}
            placeholder="e.g., Flight to Paris"
            required
          />
        </div>

        <div className="form-group">
          <label>Trip Type*</label>
          <select
            value={formData.tripType}
            onChange={(e) => handleTripTypeChange(e.target.value)}
            className="form-control"
            required
          >
            <option value="one_way">One-Way</option>
            <option value="round_trip">Round-Trip</option>
          </select>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Airline*</label>
            <input
              type="text"
              value={formData.airline}
              onChange={(e) => setFormData({...formData, airline: e.target.value})}
              placeholder="e.g., Delta Airlines"
              required
            />
          </div>
          <div className="form-group">
            <label>Flight Number*</label>
            <input
              type="text"
              value={formData.flightNumber}
              onChange={(e) => setFormData({...formData, flightNumber: e.target.value})}
              placeholder="e.g., DL1234"
              required
            />
          </div>
          <div className="form-group">
            <label>{formData.tripType === 'round_trip' ? 'Outbound Stops' : 'Stops'}</label>
            <select
              value={formData.stops}
              onChange={(e) => handleStopsChange('stops', 'connections', e.target.value)}
              className="form-control"
            >
              {STOPS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        {formData.connections.length > 0 && renderConnectionsEditor(formData.connections, 'connections')}

        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={formData.liveTracking}
              onChange={(e) => setFormData({...formData, liveTracking: e.target.checked})}
            />
            Enable Live Flight Tracking
          </label>
        </div>
      </div>

      <div className="form-section">
        <h4>{formData.tripType === 'round_trip' ? 'Outbound Flight' : 'Departure & Arrival'}</h4>

        <div className="form-row">
          <div className="form-group">
            <label>Departure Airport*</label>
            <input
              type="text"
              value={formData.departureAirport}
              onChange={(e) => setFormData({...formData, departureAirport: e.target.value})}
              placeholder="e.g., LAX"
              required
            />
          </div>
          <div className="form-group">
            <label>Departure Date*</label>
            <input
              type="date"
              value={formData.departureDate}
              onChange={(e) => setFormData({...formData, departureDate: e.target.value})}
              required
            />
          </div>
          <div className="form-group">
            <label>Departure Time</label>
            <input
              type="time"
              value={formData.departureTime}
              onChange={(e) => setFormData({...formData, departureTime: e.target.value})}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Arrival Airport*</label>
            <input
              type="text"
              value={formData.arrivalAirport}
              onChange={(e) => setFormData({...formData, arrivalAirport: e.target.value})}
              placeholder="e.g., CDG"
              required
            />
          </div>
          <div className="form-group">
            <label>Arrival Date*</label>
            <input
              type="date"
              value={formData.arrivalDate}
              onChange={(e) => setFormData({...formData, arrivalDate: e.target.value})}
              required
            />
          </div>
          <div className="form-group">
            <label>Arrival Time</label>
            <input
              type="time"
              value={formData.arrivalTime}
              onChange={(e) => setFormData({...formData, arrivalTime: e.target.value})}
            />
          </div>
        </div>
      </div>

      {formData.tripType === 'round_trip' && (
        <div className="form-section">
          <h4>Return Flight</h4>

          <div className="form-row">
            <div className="form-group">
              <label>Return Airline</label>
              <input
                type="text"
                value={formData.returnAirline}
                onChange={(e) => setFormData({...formData, returnAirline: e.target.value})}
                placeholder="e.g., Delta Airlines (leave blank if same as outbound)"
              />
            </div>
            <div className="form-group">
              <label>Return Flight Number</label>
              <input
                type="text"
                value={formData.returnFlightNumber}
                onChange={(e) => setFormData({...formData, returnFlightNumber: e.target.value})}
                placeholder="e.g., DL5678"
              />
            </div>
            <div className="form-group">
              <label>Return Stops</label>
              <select
                value={formData.returnStops}
                onChange={(e) => handleStopsChange('returnStops', 'returnConnections', e.target.value)}
                className="form-control"
              >
                {STOPS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          {formData.returnConnections.length > 0 && renderConnectionsEditor(formData.returnConnections, 'returnConnections')}

          <div className="form-row">
            <div className="form-group">
              <label>Return Departure Airport*</label>
              <input
                type="text"
                value={formData.returnDepartureAirport}
                onChange={(e) => setFormData({...formData, returnDepartureAirport: e.target.value})}
                placeholder="e.g., CDG"
                required
              />
            </div>
            <div className="form-group">
              <label>Return Departure Date*</label>
              <input
                type="date"
                value={formData.returnDepartureDate}
                onChange={(e) => setFormData({...formData, returnDepartureDate: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label>Return Departure Time</label>
              <input
                type="time"
                value={formData.returnDepartureTime}
                onChange={(e) => setFormData({...formData, returnDepartureTime: e.target.value})}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Return Arrival Airport*</label>
              <input
                type="text"
                value={formData.returnArrivalAirport}
                onChange={(e) => setFormData({...formData, returnArrivalAirport: e.target.value})}
                placeholder="e.g., LAX"
                required
              />
            </div>
            <div className="form-group">
              <label>Return Arrival Date*</label>
              <input
                type="date"
                value={formData.returnArrivalDate}
                onChange={(e) => setFormData({...formData, returnArrivalDate: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label>Return Arrival Time</label>
              <input
                type="time"
                value={formData.returnArrivalTime}
                onChange={(e) => setFormData({...formData, returnArrivalTime: e.target.value})}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Return Seats</label>
              <input
                type="text"
                value={formData.returnSeats}
                onChange={(e) => setFormData({...formData, returnSeats: e.target.value})}
                placeholder="e.g., 14C, 14D"
              />
            </div>
            <div className="form-group">
              <label>Return Confirmation Number (PNR)</label>
              <input
                type="text"
                value={formData.returnConfirmationNumber}
                onChange={(e) => setFormData({...formData, returnConfirmationNumber: e.target.value})}
                placeholder="Leave blank if same as outbound"
              />
            </div>
          </div>
        </div>
      )}

      <div className="form-section">
        <h4>Booking Details</h4>
        
        <div className="form-row">
          <div className="form-group">
            <label>Seats</label>
            <input
              type="text"
              value={formData.seats}
              onChange={(e) => setFormData({...formData, seats: e.target.value})}
              placeholder="e.g., 12A, 12B"
            />
          </div>
          <div className="form-group">
            <label>Confirmation Number (PNR)</label>
            <input
              type="text"
              value={formData.confirmationNumber}
              onChange={(e) => setFormData({...formData, confirmationNumber: e.target.value})}
              placeholder="e.g., ABC123"
            />
          </div>
          <div className="form-group">
            <label>Price*</label>
            <input
              type="number"
              value={formData.price}
              onChange={(e) => setFormData({...formData, price: e.target.value})}
              placeholder="0.00"
              min="0"
              step="0.01"
              required
            />
          </div>
        </div>

        <div className="form-group">
          <label>Additional Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            placeholder="Add any additional details about this flight..."
            rows="3"
          />
        </div>
      </div>

      <CommonOptionFields formData={formData} setFormData={setFormData} />

      <AttachmentUpload
        photos={formData.photos}
        documents={formData.documents}
        onPhotosChange={(photos) => setFormData({...formData, photos})}
        onDocumentsChange={(documents) => setFormData({...formData, documents})}
        maxPhotos={5}
        maxDocuments={3}
        showPhotos={true}
        showDocuments={true}
      />

      <div className="form-actions">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary">
          {initialData ? 'Update' : 'Add'} Flight
        </button>
      </div>
    </form>

    {showSearchModal && (
      <FlightSearchModal
        onClose={() => setShowSearchModal(false)}
        onImport={handleImportFlight}
      />
    )}
    </>
  );
};

export default FlightForm;
